import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { PktrAppMenuService } from '@prospektr/pktr-app-menu';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AuthorizeService } from 'src/app/_core/access-control/authorize.service';
import { DealAnalysisService } from 'src/app/_shared/service/deal-analysis.service';
import { DealAnalysisState } from 'src/app/_shared/state/deal-analysis.state';
import { UserState } from 'src/app/_shared/state/user.state';
import { ConfirmDialogComponent } from 'src/app/shared/modules/alerts/confirm-dialog/confirm-dialog.component';
import { ValidatorsService } from 'src/app/shared/service/validators.service';

interface PeriodicElement {
  name: string;
  updatedDate: string;
  ownedBy: any;
  action: any;
}

@Component({
  selector: 'app-deal-analysis-page',
  templateUrl: './deal-analysis-page.component.html',
  styleUrls: ['./deal-analysis-page.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class DealAnalysisPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('editDealDialog', { static: true }) editDealDialog: any;
  @ViewChild('dealName') dealName;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  isLoadingDealList = true;
  tableColumnList: string[] = ['address', 'name', 'ownedBy', 'updatedDate', 'action'];
  tableDataSource: MatTableDataSource<any> = new MatTableDataSource();
  expandedElement: PeriodicElement | null;
  tableDataSourceCount: number = 0;
  newDealName: string = '';
  existingDealName: string = '';
  isSubmit: boolean = false;
  activeDealId;
  searchField = '';
  ownedType = '';
  pageSize = 10;
  pageIndex = 0;
  offset = this.pageIndex * this.pageIndex;
  orderBy = 'updatedDate';
  orderType = 'desc';
  isSearchEnabled: boolean = false;
  onDestroyNotifier$ = new Subject();
  dealAnalysisFilterForm: FormGroup;

  constructor(
    private matDialog: MatDialog,
    private dealAnalysisState: DealAnalysisState,
    private dealAnalysisService: DealAnalysisService,
    private authorizeService: AuthorizeService,
    private userState: UserState,
    private router: Router,
    private formBuilder: FormBuilder,
    private activatedRoute: ActivatedRoute,
    public _pktrAppMenuService: PktrAppMenuService,
    public breakpointService: ValidatorsService
  ) { }

  ngOnInit() {
    this.dealAnalysisFilterForm = this.formBuilder.group({
      searchDealField: [''],
      ownedTypeField: [''],
    });
    this.dealAnalysisFilterForm.valueChanges.pipe(debounceTime(1000)).subscribe((res) => {
      this.getFilterDealInfo();
    });
    this.getDeal();
  }

  ngAfterViewInit() {
    this.dealAnalysisState.savedDeals$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (res) {
        res.map((property) => {
          let addressComponentList = property.address?.split(',');
          property.address =
            addressComponentList[0] +
            ',' +
            addressComponentList[1] +
            ',' +
            addressComponentList[2] +
            '  ' +
            addressComponentList[3];
          return property;
        });
      }
      res?.forEach(item => {
        item.deals = item?.deals.filter(deal => deal?.type !== "saveRental-Analysis");
      });
      res = res.filter(item => item.deals.length > 0);
      this.tableDataSource = new MatTableDataSource(res);
      this.tableDataSource.paginator = this.paginator;
      this.tableDataSource.sort = this.sort;
    });
    this.dealAnalysisState.savedDealsCountSub$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      this.tableDataSourceCount = res;
    });

    this.sort.sortChange.subscribe(() => {
      this.paginator.firstPage();
    });
  }

  ngOnDestroy() {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  getFilterDealInfo() {
    this.searchField = this.dealAnalysisFilterForm.value.searchDealField;
    this.ownedType = this.dealAnalysisFilterForm.value.ownedTypeField;
    this.getDeal();
  }

  getDeal() {
    this.userState.userDetail$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      if (res) {
        const params: any = {
          limit: this.pageSize,
          orderBy: this.orderBy,
          orderType: this.orderType,
          searchQuery: this.searchField,
          isOwned: this.ownedType,
        };
        if (!this.isSearchEnabled) {
          this.offset = this.pageSize * this.pageIndex;
          params.offset = this.offset;
        }
        this.dealAnalysisService
          .getDealAnalysisByAddress(params)
          .pipe(takeUntil(this.onDestroyNotifier$))
          .subscribe((res: any) => {
            this.isLoadingDealList = res?.isLoading;
            if (res?.isPageIndexChanged) {
              this.pageIndex = this.pageIndex - 1;
              this.paginator.pageIndex = this.pageIndex;
            }
          });
      }
    });
  }

  deleteDeal(id, deleteBy) {
    if (this.authorizeService.canAct('dealAnalysis')) {
      this.openDeleteDealDialog(id, deleteBy);
    }
  }

  openDeleteDealDialog(id, deleteBy) {
    const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
      width: '385px',
      disableClose: true,
      hasBackdrop: true,
      data: {
        type: 'deleteDeal',
        message:
          deleteBy === 'address'
            ? 'All rental analysis against the selected address will be deleted'
            : 'The selected rental analysis will be deleted',
        title: 'Do you want to delete?',
        proceedAction: (status, callBack) => {
          if (status) {
            this.dealAnalysisService
              .deleteDeal(deleteBy === 'address' ? { PMXPropertyId: id } : { id: id })
              .pipe(takeUntil(this.onDestroyNotifier$))
              .subscribe((res) => {
                this.getDeal();
              });
            callBack({ status: true });
            dialogRef.close();
          } else {
            return false;
          }
        },
        proceedCancelAction: () => {
          dialogRef.close();
        },
      },
    });
  }

  openRenameDealDialog(deal) {
    if (this.authorizeService.canAct('dealAnalysis')) {
      this.activeDealId = deal.id;
      this.newDealName = deal.name;
      this.existingDealName = deal.name;
      this.matDialog.open(this.editDealDialog);
    }
  }

  resetForm() {
    this.matDialog.closeAll();
    this.activeDealId = '';
    this.newDealName = '';
    this.existingDealName = '';
    this.isSubmit = false;
  }

  renameDeal() {
    if (this.dealName.errors && this.dealName.errors.pattern) return;
    this.isSubmit = true;
    if (this.existingDealName != this.newDealName) {
      this.dealAnalysisService
        .renameDeal(this.activeDealId, this.newDealName)
        .pipe(takeUntil(this.onDestroyNotifier$))
        .subscribe((res) => {
          this.getDeal();
        });
      this.resetForm();
    }
  }

  resetSearchField() {
    // this.tableDataSource.paginator = this.paginator;
    this.isSearchEnabled = false;
    this.searchField = '';
    this.ownedType = '';
    this.dealAnalysisFilterForm.get('searchDealField').setValue('');
    this.dealAnalysisFilterForm.get('ownedTypeField').setValue('');
    this.pageSize = 10;
    this.pageIndex = 0;
    this.orderBy = 'updatedDate';
    this.orderType = 'desc';
    // this.getDeal();
  }

  openDealAnalysisSheet(dealDetails) {
    this.breakpointService
      .isMobileOrTablet()
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((isMobileOrTablet) => {
        if (isMobileOrTablet) {
          this._pktrAppMenuService.close(true);
        }

      });

    if (this.authorizeService.canAct('dealAnalysis')) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          'deal-analysis-id': dealDetails.id,
        },
        queryParamsHandling: 'merge',
      });
    }
  }
}
